import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { format } from "date-fns";
import { FaTrash, FaCalendarAlt, FaTable, FaFileInvoice, FaUser } from "react-icons/fa";

import CardComponent from "../../components/cards/CardComponent";
import Header from "../../components/Header";
import Layout from "../../layout/Layout";
import { TextfieldwithLabel } from "../../components/Buttons/Textfield";
import { SubmitButton } from "../../components/Buttons/Textfield";
import { getHeaders } from "../../utility/getHeader";
import { getUserName } from "../../functions/storageUtils";
import fetchData from "../../functions/fetchData";
import DataTable from "../../components/data-tables/dataTable";

export default function CancelBill() {
  const currentDate = format(new Date(), "yyyy-MM-dd");
  const [userName, setUserName] = useState("");
  const [cancelledBills, setCancelledBills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastCloseDate, setLastCloseDate] = useState(currentDate);

  const [formData, setFormData] = useState({
    userId: "",
    userName: "",
    tableNo: "",
    reason: "",
    billNo: "",
  });

  const [dateRange, setDateRange] = useState({
    startDate: currentDate,
    endDate: currentDate,
  });

  // Columns for the cancelled bills table
  const columns = [
    { label: "Invoice No", field: "finv" },
    { label: "Table No", field: "tables" },
    { label: "Cancelled By", field: "userid" },
    { label: "Reason", field: "remark" },
    { label: "Cancelled Date", field: "kayote" },
    { label: "Bill Amount", field: "net_amount" },
    { label: "Mode", field: "mode" },
  ];

  // Get logged-in user information on component mount
  useEffect(() => {
    const fetchedUserName = getUserName();
    setUserName(fetchedUserName);
    setFormData((prev) => ({
      ...prev,
      userName: fetchedUserName,
    }));
    
    // Fetch last close date
    const fetchLastCloseDate = async () => {
      try {
        const response = await axios.get('/last-close-date', getHeaders());
        if (response.data && response.data.closeDate) {
          setLastCloseDate(response.data.closeDate);
        }
      } catch (error) {
        console.error("Error fetching last close date:", error);
        // Keep using currentDate as fallback
      }
    };
    
    fetchLastCloseDate();
  }, []);

  // Fetch cancelled bills based on date range
  const fetchCancelledBills = async () => {
    setLoading(true);
    try {
      // Fetch from final_bill table where mode='cancelled'
      const response = await fetchData("final_bill", null, "id", {mode: 'cancelled'});
      
      // Filter by date range
      const data = response?.data || response || [];
      const cancelledData = Array.isArray(data) ? data.filter(bill => {
        if (!bill.kayote) return false;
        const billDate = new Date(bill.kayote).toISOString().split('T')[0];
        return billDate >= dateRange.startDate && billDate <= dateRange.endDate;
      }) : [];
      
      setCancelledBills(cancelledData);
      
      if (cancelledData.length > 0) {
        toast.success(`Loaded ${cancelledData.length} cancelled bill(s)`);
      }
    } catch (error) {
      console.error("Error fetching cancelled bills:", error);
      toast.error("Failed to load cancelled bills");
      setCancelledBills([]);
    } finally {
      setLoading(false);
    }
  };

  // Load cancelled bills on component mount and when date range changes
  useEffect(() => {
    if (lastCloseDate) {
      fetchCancelledBills();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange.startDate, dateRange.endDate, lastCloseDate]);

  // Fetch bill details when bill number changes with debounce
  useEffect(() => {
    if (!formData.billNo || formData.billNo.trim() === "") {
      setFormData(prev => ({ ...prev, tableNo: "" }));
      return;
    }

    const delayDebounce = setTimeout(() => {
      fetchBillDetails(formData.billNo);
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(delayDebounce);
  }, [formData.billNo]);

  // Fetch bill details when bill number is entered
  const fetchBillDetails = async (billNo) => {
    try {
      console.log("Fetching bill details for invoice:", billNo);
      //const response = await fetchdata("final_bill", "finv", billNo);
      const response = await fetchData("final_bill", null, "id", {inv: billNo});

      console.log("Bill details response:", response.data);

      if (response.data && response.data.length > 0) {
        const billData = response.data[0];
        console.log("Setting table number:", billData.ftable);
        setFormData(prev => ({
          ...prev,
          tableNo: billData.ftable || ""
        }));
      } else {
        console.log("No bill found");
        setFormData(prev => ({ ...prev, tableNo: "" }));
      }
    } catch (error) {
      console.error("Error fetching bill details:", error);
      setFormData(prev => ({ ...prev, tableNo: "" }));
    }
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Handle date range changes
  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDateRange((prevRange) => ({
      ...prevRange,
      [name]: value,
    }));
  };

  // Handle bill cancellation
  const handleCancelBill = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.billNo) {
      toast.error("Please enter Bill Number");
      return;
    }
    if (!formData.reason) {
      toast.error("Please provide a reason for cancellation");
      return;
    }

    try {
      const invoiceNo = formData.billNo;
      const currentTime = format(new Date(), "yyyy-MM-dd HH:mm:ss");
      let deletedFromBarCart = false;
      let deletedFromFoodCart = false;
      let deletedFromCollection = false;
      
      // Step 1: Delete from bar_cart
      try {
        const barCartResponse = await axios.delete(
          `/deletebyid/bar_cart/invoice/${invoiceNo}`,
          getHeaders()
        );
        if (barCartResponse.data.success && barCartResponse.data.affectedRows > 0) {
          deletedFromBarCart = true;
          console.log("Deleted from bar_cart");
        }
      } catch (error) {
        console.log("No records in bar_cart or error:", error.message);
      }

      // Step 2: Delete from food_cart
      try {
        const foodCartResponse = await axios.delete(
          `/deletebyid/food_cart/invoice/${invoiceNo}`,
          getHeaders()
        );
        if (foodCartResponse.data.success && foodCartResponse.data.affectedRows > 0) {
          deletedFromFoodCart = true;
          console.log("Deleted from food_cart");
        }
      } catch (error) {
        console.log("No records in food_cart or error:", error.message);
      }

      // Step 3: Delete from collection
      try {
        const collectionResponse = await axios.delete(
          `/deletebyid/collection/barbill/${invoiceNo}`,
          getHeaders()
        );
        if (collectionResponse.data.success && collectionResponse.data.affectedRows > 0) {
          deletedFromCollection = true;
          console.log("Deleted from collection");
        }
      } catch (error) {
        console.log("No records in collection or error:", error.message);
      }

      // Check if at least one deletion was successful
      if (!deletedFromBarCart && !deletedFromFoodCart && !deletedFromCollection) {
        toast.error("No records found to delete for this invoice number");
        return;
      }

      // Step 4: Update final_bill table
      try {
        const updateData = {
          flag: 1,
          mode: 'cancelled',
          kayote: currentTime,
          userid: formData.userName || userName,
          remark: formData.reason,
          paid_status: 0
        };

        const updateResponse = await axios.put(
          `/updatecommondata/final_bill/inv/${invoiceNo}`,
          updateData,
          getHeaders()
        );

        if (updateResponse.data.affectedRows > 0) {
          const deletedItems = [];
          if (deletedFromBarCart) deletedItems.push("bar_cart");
          if (deletedFromFoodCart) deletedItems.push("food_cart");
          if (deletedFromCollection) deletedItems.push("collection");
          
          toast.success(`Bill cancelled successfully! Records deleted from: ${deletedItems.join(", ")}`);
        } else {
          toast.warning("Records deleted but bill not found in final_bill table");
        }
      } catch (updateError) {
        console.error("Error updating final_bill:", updateError);
        
        // Still show partial success if records were deleted
        const deletedItems = [];
        if (deletedFromBarCart) deletedItems.push("bar_cart");
        if (deletedFromFoodCart) deletedItems.push("food_cart");
        if (deletedFromCollection) deletedItems.push("collection");
        
        if (deletedItems.length > 0) {
          toast.warning(`Records deleted from ${deletedItems.join(", ")}, but could not update final_bill table: ${updateError.response?.data?.message || updateError.message}`);
        } else {
          throw updateError; // Re-throw if no deletions occurred
        }
      }
      
      // Reset form
      setFormData({
        userId: "",
        userName: userName,
        tableNo: "",
        reason: "",
        billNo: "",
      });

      // Refresh the cancelled bills list
      fetchCancelledBills();
    } catch (error) {
      console.error("Error cancelling bill:", error);
      toast.error(
        error.response?.data?.message || "Failed to cancel bill"
      );
    }
  };

  return (
    <>
      <Layout>
        <Header title="Cancel Bill" />
        <ToastContainer />
        
        <div className="row">
          {/* Cancel Bill Form */}
          <div className="col-12">
            <CardComponent
              title="Cancel Bill"
              headerColor="#dc3545"
              pull="left"
              bodyClass="panel-body"
            >
              <form onSubmit={handleCancelBill}>
                <div className="panel panel-default card-view">
                  <div className="row">
                    {/* User Name */}
                    <div className="col-md-3 col-sm-6">
                      <div className="form-group">
                        <label className="control-label mb-2">
                          <FaUser className="me-2" />
                          User Name
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          name="userName"
                          value={formData.userName || userName}
                          readOnly
                          style={{ backgroundColor: "#f0f0f0" }}
                        />
                      </div>
                    </div>

                    {/* Bill Number */}
                    <div className="col-md-3 col-sm-6">
                      <TextfieldwithLabel
                        id="billNo"
                        onChange={handleInputChange}
                        value={formData.billNo}
                        type="text"
                        name="billNo"
                        lable="Bill No."
                        placeholder="Enter bill number"
                      />
                    </div>

                    {/* Reason */}
                    <div className="col-md-4 col-sm-8">
                      <div className="form-group">
                        <label className="control-label mb-2">
                          Reason to Delete
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          name="reason"
                          value={formData.reason}
                          onChange={handleInputChange}
                          placeholder="Enter reason for cancelling the bill"
                        />
                      </div>
                    </div>

                    {/* Submit Button */}
                    <div className="col-md-2 col-sm-4">
                      <label className="control-label mb-2" style={{ visibility: "hidden" }}>
                        Submit
                      </label>
                      <SubmitButton
                        buttonClass="btn btn-danger btn-block"
                        buttonIcon={<FaTrash className="me-2" />}
                        buttonText="Cancel Bill"
                      />
                    </div>
                  </div>
                </div>
              </form>
            </CardComponent>
          </div>

          {/* Cancelled Bills Records */}
          <div className="col-12">
            <CardComponent
              title="Cancelled Bills History"
              headerColor="#17a2b8"
              pull="left"
              bodyClass="panel-body"
            >
              {/* Date Range Filter */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <label className="form-label">
                    <FaCalendarAlt className="me-2" />
                    Start Date
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    name="startDate"
                    value={dateRange.startDate}
                    onChange={handleDateChange}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    <FaCalendarAlt className="me-2" />
                    End Date
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    name="endDate"
                    value={dateRange.endDate}
                    onChange={handleDateChange}
                  />
                </div>
              </div>

              {/* Cancelled Bills Table */}
              <div className="table-responsive">
                {loading ? (
                  <div className="text-center p-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                  </div>
                ) : cancelledBills.length > 0 ? (
                  <table className="table table-striped table-bordered">
                    <thead>
                      <tr>
                        {columns.map((col, index) => (
                          <th key={index}>{col.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {cancelledBills.map((bill, index) => (
                        <tr key={index}>
                          {columns.map((col, colIndex) => (
                            <td key={colIndex}>{bill[col.field] || '-'}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="alert alert-info text-center">
                    <FaFileInvoice className="me-2" size={20} />
                    No cancelled bills found for the selected date range
                  </div>
                )}
              </div>
            </CardComponent>
          </div>
        </div>
      </Layout>
    </>
  );
}
